import { useModalActions } from '@carboncopyco/components';
import { MoreVerticalOutlineIcon } from '@carboncopyco/icons';
import {
  type IGlobalTab,
  TabGroupName,
  TabType,
} from '@carboncopyco/types/desktop';
import { ColorHex } from '@carboncopyco/ui';
import { useAnalytics } from '@carboncopyco/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnalyticsEvents } from '../../../shared/constants/analytics.events';
import { AppEventsEnum } from '../../../shared/enums/AppEventsEnum';
import { isBrowserTab, isOverlayTab } from '../../../shared/globalTabUtil';
import {
  getEditsKey,
  getRulesKey,
  getVariablesKey,
} from '../../../shared/interceptor-state-keys';
import useEditorGroupTab from '../../hooks/useEditorGroupTab';
import useElectronListener from '../../hooks/useElectronListener';
import { useFlagsFallback } from '../../hooks/useFlagsFallback';
import usePageLoadedAnimation from '../../hooks/usePageLoadedAnimation';
import useSpeakerPanel from '../../hooks/useSpeakerPanel';
import useSpeakerPanelData from '../../hooks/useSpeakerPanelData';
import useSwitchTab from '../../hooks/useSwitchTab';
import { useTabManager } from '../../services/TabManager';
import { debuggerInterceptorState } from '../../shared/utils';
import { useTabs, useUserSettings } from '../../store/context';
import { useAnalyticsSession } from '../../store/context/AnalyticsSessionContext';
import Monitoring from '../Monitoring';
import WindowsTitleBar from '../WindowsTitleBar/WindowsTitleBar';
import ZoomPopup from '../ZoomPopup';
import AppTabs, {
  DarwinTrafficLightsPlaceholder,
  TabPanel,
  TabPanelContent,
} from './AppTabs';

const canNavigateOnKeyboardNavigation = (tab: IGlobalTab) => {
  return (
    tab.group?.isVisibleIfGroupIsInactive ||
    tab.group?.isVisibleIfGroupIsActive ||
    [
      TabType.BASIC,
      TabType.CLONE,
      TabType.UPDATE_CLONE,
      TabType.PREVIEW_CLONE,
    ].includes(tab.type)
  );
};

const TabsContainer = () => {
  const {
    globalTabs,
    globalTabsRef,
    activeGlobalTabId,
    setIsOpenCloseTabDialog,
    tabsQueue,
    registerTabQueue,
    triggerTabQueue,
  } = useTabs();
  const {
    closeGlobalTab,
    navigateGlobalTab,
    createInnerTab,
    getActiveGlobalTab,
    getGlobalTabById,
    closeGroupTabs,
    updateGroupTabProperties,
    closeGroupTabsWithException,
  } = useTabManager();
  const { trackEvent } = useAnalytics();
  const [fullScreen, setFullScreen] = useState(false);
  const { animationClasses, animatedStyles } = usePageLoadedAnimation();
  const { theme } = useUserSettings();
  const [isWindows, setIsWindows] = useState(false);
  const homeTabClasses = isWindows ? '!w-[60px] !min-w-[60px]' : '';
  const switchTab = useSwitchTab();
  const { desktopAppMonitoring } = useFlagsFallback();
  const { isSessionIdle } = useAnalyticsSession();
  const { set: modalsSet } = useModalActions();
  const hasAnyGlobalModalOpen = Array.from(modalsSet).length > 0;

  const { updateSpeakerPanelDemosInfoData } = useSpeakerPanelData();
  const { updateEditorTabVisibility, getFirstGroupTabByGroupId } =
    useEditorGroupTab();

  useEffect(() => {
    triggerTabQueue(activeGlobalTabId);
  }, [activeGlobalTabId, tabsQueue]);

  const handleSwitchTab = async (tabId: number) => {
    if (tabId === activeGlobalTabId) return;
    const selectedTab = getGlobalTabById(tabId);
    switchTab(selectedTab);

    const selectedTabIsPartOfSpeakerPanel = selectedTab?.group?.mergingOptions;
    if (selectedTabIsPartOfSpeakerPanel && selectedTab?.group?.id) {
      const speakerPanelId = selectedTab.group.id;
      if (!selectedTab.group.mergingOptions) return;
      await window.dsDesktop.speakerPanel.updateData(speakerPanelId, {
        activePresentationIndex: selectedTab.group.mergingOptions.index,
      });
      await updateSpeakerPanelDemosInfoData(speakerPanelId);
    }
  };

  const handleCloneUrlSelect = useCallback((url: string) => {
    createInnerTab({ url });
    trackEvent(AnalyticsEvents.CLONE_CLICK_GO, {
      location: 'all_clones',
    });
  }, []);

  const handleOverlayUrlSelect = useCallback(() => {
    trackEvent(AnalyticsEvents.OVERLAY_CLICK_GO, {
      location: 'all_clones',
    });
  }, []);

  const getNextTabToSwitchTo = (tabId: number) => {
    const activeTab = getActiveGlobalTab();
    if (!globalTabsRef.current) return globalTabs[0];

    const visibleGlobalTabs = globalTabsRef.current.filter(
      (globalTab) => !globalTab.hidden
    );

    // find index of closed tab in visible tabs
    const closedTabIndex = visibleGlobalTabs.findIndex(
      (globalTab) => globalTab.id === tabId
    );

    const filteredVisibleList = visibleGlobalTabs.filter(
      (globalTab) => globalTab.id !== tabId
    );

    if (tabId !== activeTab?.id) return activeTab;

    // Take next tab or last visible tab if closed tab was last.
    // If there is no visible tabs (Home tab has hidden: true value), take home tab
    return (
      filteredVisibleList[closedTabIndex] ||
      filteredVisibleList[filteredVisibleList.length - 1] ||
      globalTabs[0]
    );
  };

  const handleTabClose = async (tabId: number) => {
    let tab = getGlobalTabById(tabId);

    if (tab?.group?.mergingOptions?.isDetached) {
      const speakerPanelId = tab.group.id;
      const tabToAttach = tab;
      const activeGlobalTab = globalTabsRef.current.find(
        (t) => t.id === activeGlobalTabId
      );
      if (!activeGlobalTab) return;

      const updatedTabs = globalTabsRef.current.map((t) => {
        const isTabToAttach = t.id === tabToAttach.id;

        if (!t.group?.mergingOptions) return t;
        if (isTabToAttach) {
          return {
            ...t,
            hidden: true,
            group: {
              ...t.group,
              isVisibleIfGroupIsActive: false,
              isVisibleIfGroupIsInactive: false,
              mergingOptions: {
                ...t.group.mergingOptions,
                isDetached: false,
              },
            },
          };
        }
        return t;
      });

      const nextTabToSwitchTo = getNextTabToSwitchTo(tabId);

      switchTab(nextTabToSwitchTo, {
        globalTabs: updatedTabs,
      });

      if (nextTabToSwitchTo?.group?.mergingOptions) {
        await window.dsDesktop.speakerPanel.updateData(speakerPanelId, {
          activePresentationIndex: nextTabToSwitchTo.group.mergingOptions.index,
        });
      }
      await updateSpeakerPanelDemosInfoData(speakerPanelId);
      return;
    }

    const isTabOpenedFromEditor = tab.payload?.isOpenedFromEditor;
    const editorTab = tab.group && getFirstGroupTabByGroupId(tab.group.id);
    const hasUnsavedEdits = editorTab?.payload?.draftModified;
    const isBrowserOpenFromEditor = isTabOpenedFromEditor && isBrowserTab(tab);

    // If we are closing a tab opened from editor there are two cases:
    // 1. The editor tab has unsaved changes
    //    In this case we want to show the close tab dialog for the editor tab.
    // 2. The editor tab has no unsaved changes
    //    In this case we want to close the editor tab and the tab opened from editor.
    //    To do this we set closeGroupOnClose to true.
    if (isTabOpenedFromEditor) {
      if (!editorTab) return;
      if (!hasUnsavedEdits) {
        const updatedTab = updateGroupTabProperties(tab.id, {
          closeGroupOnClose: true,
        });
        if (!updatedTab) return;
        tab = updatedTab;
      } else if (!isBrowserTab(tab))
        updateEditorTabVisibility({
          editorTab,
          show: true,
        });
    }

    const hasUnsavedChanges =
      tab?.payload?.draftModified ||
      tab?.payload?.cloningInProgress ||
      tab?.payload?.tourRecordingInProgress;

    if (hasUnsavedChanges) {
      handleSwitchTab(tab.id);
      registerTabQueue(tab.id, () => setIsOpenCloseTabDialog(true));

      return;
    }
    // close browser tabs on preview in editor
    if (tab?.group?.closeGroupOnClose && !isBrowserOpenFromEditor) {
      closeGroupTabs(tab);
    } else {
      closeGlobalTab(tab.id);
      if (isOverlayTab(tab)) {
        debuggerInterceptorState.clearStateForKeys([
          getRulesKey(tab.id),
          getEditsKey(tab.id),
          getVariablesKey(tab.id),
        ]);
      }

      // We need this separate check because we want to close the tab opened from editor first
      // and then open this dialog for the editor tab.
      // only main editor tab should close all tabs browser tabs close regularly
      if (!isBrowserTab(tab) && isTabOpenedFromEditor && hasUnsavedEdits) {
        closeGroupTabsWithException(tab, isBrowserTab, editorTab);
        registerTabQueue(editorTab.id, () => setIsOpenCloseTabDialog(true));
      }
    }
  };

  const handleKeyboardTabNavigation = (direction: 'previous' | 'next') => {
    const activeTab = getActiveGlobalTab();
    if (!activeTab) return;

    const tabsToSwitch = globalTabsRef.current.filter((tab) =>
      canNavigateOnKeyboardNavigation(tab)
    );
    const activeTabIndex = tabsToSwitch.findIndex(
      (tab) => tab.id === activeTab.id
    );
    const nextTabIndex =
      direction === 'next' ? activeTabIndex + 1 : activeTabIndex - 1;
    const nextTab = tabsToSwitch[nextTabIndex];

    const nextTabIsOutsidePresentation =
      activeTab.group &&
      (activeTab.group.name === TabGroupName.PlaybookPresentation ||
        activeTab.group.name === TabGroupName.DemoPresent) &&
      activeTab.group.id !== nextTab.group?.id;

    const canNavigate =
      nextTab &&
      !nextTabIsOutsidePresentation &&
      !isSessionIdle &&
      !hasAnyGlobalModalOpen;

    if (canNavigate) {
      handleSwitchTab(nextTab.id);
    }
  };

  useEffect(() => {
    window.dsDesktop.app.notifyInitialized();
    if (window?.dsDesktop?.app?.isWindows)
      setIsWindows(window?.dsDesktop?.app?.isWindows);

    window.dsDesktop.view.onToggleFullScreen(({ isFullScreen }) =>
      setFullScreen(isFullScreen)
    );
  }, []);

  useEffect(() => {
    const tab = globalTabs.find(
      (globalTab: IGlobalTab) => globalTab.id === activeGlobalTabId
    );
    if (!tab) return;

    navigateGlobalTab(tab);
  }, [activeGlobalTabId]);

  useSpeakerPanel();

  useElectronListener<'previous' | 'next'>(
    AppEventsEnum.MAIN_TO_RENDERER_GLOBAL_TABS_KEYBOARD_SWITCH,
    handleKeyboardTabNavigation
  );

  const showDropdownMenu = useMemo(() => {
    const activeTab = globalTabs?.find((tab) => tab.id === activeGlobalTabId);
    if (!activeTab) return false;

    return (
      activeTab.group?.name &&
      [
        TabGroupName.PlaybookPresentation,
        TabGroupName.PlaybookPreview,
        TabGroupName.DemoPreview,
        TabGroupName.DemoPresent,
        TabGroupName.TourPreview,
        TabGroupName.ClonePreview,
      ].includes(activeTab.group.name)
    );
  }, [globalTabs, activeGlobalTabId]);

  const activeTabData = getGlobalTabById(activeGlobalTabId);

  const isDemoPresentTab =
    activeTabData?.group?.name === TabGroupName.DemoPresent;
  const isPlaybookPresentation =
    activeTabData?.group?.name === TabGroupName.PlaybookPresentation;
  const isPlaybookPreviewTab =
    activeTabData?.group?.name === TabGroupName.PlaybookPreview &&
    !activeTabData?.payload?.play?.isDeleted;
  const isDemoPreviewTab =
    activeTabData?.group?.name === TabGroupName.DemoPreview &&
    !activeTabData?.payload?.demo?.isDeleted &&
    !activeTabData.payload?.isOpenedFromEditor;

  const showAddNewBrowserTabButton =
    isDemoPresentTab ||
    isDemoPreviewTab ||
    isPlaybookPresentation ||
    isPlaybookPreviewTab;

  return (
    <div
      className={`flex flex-col flex-grow shrink-0 basis-auto ${animationClasses}`}
      style={animatedStyles}
    >
      <AppTabs
        activeTabId={activeGlobalTabId}
        onTabClick={handleSwitchTab}
        onTabClose={handleTabClose}
        theme={theme}
        showDropdownMenu={showDropdownMenu}
        startExtraContent={
          !isWindows && (
            <DarwinTrafficLightsPlaceholder isFullScreen={fullScreen} />
          )
        }
        endExtraContent={isWindows && <WindowsTitleBar />}
        homeTabClassName={homeTabClasses}
        moreTabsDropdownIcon={
          isWindows && <MoreVerticalOutlineIcon color={ColorHex.natural50} />
        }
        moreTabsOpenOnClick={isWindows}
        showAddNewBrowserTabButton={showAddNewBrowserTabButton}
      >
        {globalTabs.map((tab) => {
          return (
            <TabPanel
              activeTabId={activeGlobalTabId}
              tabId={tab.id}
              key={tab.id}
            >
              <TabPanelContent
                tab={tab}
                handleOverlayUrlSelect={handleOverlayUrlSelect}
                handleCloneUrlSelect={handleCloneUrlSelect}
              />
            </TabPanel>
          );
        })}
      </AppTabs>

      <ZoomPopup />

      {desktopAppMonitoring && <Monitoring />}
    </div>
  );
};

export default TabsContainer;
